import { createFileRoute } from '@tanstack/react-router';
import { Trans } from 'react-i18next';
import { PageHeader } from '@/components/page-header';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/lib/i18n';

export const Route = createFileRoute('/help/shortcuts')({ component: Shortcuts });

/**
 * The QBO keyboard set (document 05). Shortcuts for screens not built yet are marked. The second
 * item is the description key under accountingPages.shortcuts.action.
 */
const SHORTCUTS = [
  ['Ctrl+Alt+A', 'chartOfAccounts', true],
  ['Ctrl+Alt+F', 'search', true],
  ['/', 'searchSlash', true],
  ['Ctrl+Alt+/', 'showShortcuts', true],
  ['Ctrl+Alt+S', 'saveClose', true],
  ['Ctrl+Alt+D', 'saveNew', true],
  ['Ctrl+Alt+X', 'exitForm', true],
  ['Ctrl+Alt+I', 'newInvoice', true],
  ['Ctrl+Alt+E', 'newQuote', true],
  ['Ctrl+Alt+R', 'receivePayment', true],
  ['Ctrl+Alt+W', 'vendorPayment', false],
  ['Ctrl+Alt+C', 'customers', true],
  ['Ctrl+Alt+V', 'vendors', true],
  ['Ctrl+Alt+L', 'lists', false],
  ['Ctrl+Alt+H', 'help', false],
  ['Ctrl+Alt+M', 'send', true],
] as const;

/** Date-field keys; the second item is the key under accountingPages.shortcuts.date. */
const DATE_KEYS = [
  ['T', 'today'],
  ['+ / -', 'nextPrevDay'],
  ['W / K', 'week'],
  ['M / H', 'month'],
  ['Y / R', 'year'],
] as const;

function Shortcuts() {
  const t = useT();
  return (
    <>
      <PageHeader
        title={t('accountingPages.shortcuts.title')}
        description={t('accountingPages.shortcuts.description')}
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4">
          <h2 className="mb-2 font-semibold">{t('accountingPages.shortcuts.anywhere')}</h2>
          <table className="w-full text-sm">
            <tbody>
              {SHORTCUTS.map(([keys, action, ready]) => (
                <tr key={keys} className="border-b last:border-0">
                  <td className="w-[140px] py-1.5">
                    <kbd className="rounded border bg-muted px-1.5 py-0.5 text-xs">{keys}</kbd>
                  </td>
                  <td className="py-1.5">{t(`accountingPages.shortcuts.action.${action}`)}</td>
                  <td className="py-1.5 text-end">
                    {ready ? null : (
                      <Badge variant="outline">{t('accountingPages.shortcuts.comingSoon')}</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="rounded-lg border bg-card p-4">
          <h2 className="mb-2 font-semibold">{t('accountingPages.shortcuts.dateFields')}</h2>
          <table className="w-full text-sm">
            <tbody>
              {DATE_KEYS.map(([keys, action]) => (
                <tr key={keys} className="border-b last:border-0">
                  <td className="w-[140px] py-1.5">
                    <kbd className="rounded border bg-muted px-1.5 py-0.5 text-xs">{keys}</kbd>
                  </td>
                  <td className="py-1.5">{t(`accountingPages.shortcuts.date.${action}`)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <h2 className="mt-4 mb-2 font-semibold">{t('accountingPages.shortcuts.amountFields')}</h2>
          <p className="text-sm text-muted-foreground">
            <Trans
              t={t}
              i18nKey="accountingPages.shortcuts.amountHint"
              components={{ c1: <code />, c2: <code />, k: <kbd /> }}
            />
          </p>
        </section>
      </div>
    </>
  );
}
